<template>
  <div>
    <!-- Modal overlay -->
    <transition name="modal-fade">
      <div v-if="isVisible" class="modal-backdrop" @click="closeOnBackdropClick ? hide() : null"></div>
    </transition>

    <!-- Modal dialog -->
    <transition name="modal-fade">
      <div v-if="isVisible" class="modal" tabindex="-1" role="dialog" aria-modal="true">
        <div class="modal-dialog" :class="[sizeClass, { 'modal-dialog-centered': true }]">
          <div class="modal-content">
            <!-- Header -->
            <div class="modal-header">
              <span class="modal-title">
                <slot name="title">{{ title }}</slot>
              </span>
              <button type="button" class="close" @click="hide()" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            
            <!-- Body -->
            <div class="modal-body">
              <slot></slot>
            </div>
            
            <!-- Footer -->
            <div class="modal-footer custom-footer">
              <slot name="footer">
                <div class="w-100 d-flex justify-content-between">
                  <div class="ml-3">
                    <button type="button" class="btn btn-primary btn-primary-custom" @click="hide()">{{ cancelText }}</button>
                  </div>
                  <div class="mr-3">
                    <button type="button" class="btn btn-primary btn-primary-custom" @click="confirm()">{{ confirmText }}</button>
                  </div>
                </div>
              </slot>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch, Emit } from 'vue-property-decorator';

@Component({
  name: 'ModalBase'
})
export default class ModalBase extends Vue {
  @Prop({ type: Boolean, default: false }) readonly visible!: boolean;
  @Prop({ type: String, default: '' }) readonly title!: string;
  @Prop({ type: String, default: '' }) readonly message!: string;
  @Prop({ type: String, default: 'md' }) readonly size!: string;
  @Prop({ type: Boolean, default: true }) readonly closeOnBackdropClick!: boolean;
  @Prop({ type: Boolean, default: true }) readonly closeOnEsc!: boolean;
  @Prop({ type: String, default: 'Cancel' }) readonly cancelText!: string;
  @Prop({ type: String, default: 'OK' }) readonly confirmText!: string;
  

  private isVisible = false;

  @Watch('visible')
  onVisibilityChange(visible: boolean): void {
    this.isVisible = visible;
    this.updateBodyClass();
  }

  @Emit('input')
  emitInput(visible: boolean): boolean {
    return visible;
  }

  @Emit('confirm')
  confirm(): void {
    this.hide();
  }

  @Emit('cancel')
  cancel(): void {
    this.hide();
  }

  created(): void {
    this.isVisible = this.visible;
    document.addEventListener('keydown', this.handleKeyDown);
  }

  beforeDestroy(): void {
    document.removeEventListener('keydown', this.handleKeyDown);
    if (this.isVisible) {
      this.removeBodyClass();
    }
  }

  mounted(): void {
    this.updateBodyClass();
  }

  show(): void {
    this.isVisible = true;
    this.emitInput(true);
    this.updateBodyClass();
  }

  hide(): void {
    this.isVisible = false;
    this.emitInput(false);
    this.removeBodyClass();
    this.$emit('hide');
  }

  get sizeClass(): string {
    return this.size ? `modal-${this.size}` : '';
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (this.isVisible && this.closeOnEsc && event.key === 'Escape') {
      this.hide();
    }
  }

  private updateBodyClass(): void {
    if (this.isVisible) {
      document.body.classList.add('modal-open');
    } else {
      this.removeBodyClass();
    }
  }

  private removeBodyClass(): void {
    document.body.classList.remove('modal-open');
  }
}
</script>

<style scoped>
.modal {
  display: block;
  overflow-x: hidden;
  overflow-y: auto;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1050;
  outline: 0;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1040;
}

/* Fade transition effects */
.modal-fade-enter-active, .modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter, .modal-fade-leave-to {
  opacity: 0;
}

.modal-title {
  width: 100%;
  text-align: center;
  font-weight: bold;
  color: #338AB5; /* Added this line to set text color */
}

.custom-footer {
  display: flex;
  justify-content: center;
  gap: 10px;
  padding: 1rem;
}

.btn-primary-custom {
  background-color: #04A1E8;
  border-color: #04A1E8;
  border-radius: 0; /* This removes the rounded corners */
}

/* Add this to ensure hover/active states also have square corners */
.btn-primary-custom:hover,
.btn-primary-custom:focus,
.btn-primary-custom:active {
  border-radius: 0;
}
</style>